import { useCallback, useEffect, useRef, useState } from 'react';
import type { VisitVoiceRecordingRow } from '@helios/data-access';
import { NavIcon } from '../../../shell/shared';
import { resolvePlaybackUrl } from '../../../lib/voice-playback';

/* ------------------------------------------------------------------ */
/*  AudioRecordingPlayer (PRD.271)                                    */
/*                                                                    */
/*  Compact, accessible player rendered directly below the Chief       */
/*  Complaint text. Play/pause, seek, current/duration, playback       */
/*  speed, recording timestamp, upload status + retry, permission-      */
/*  gated delete with confirm. Keyboard-operable, screen-reader        */
/*  labelled, with a live region for status changes.                   */
/* ------------------------------------------------------------------ */

const SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const;

interface Props {
  recording: VisitVoiceRecordingRow;
  /** Object URL of the in-memory blob before/while it uploads. Preferred source. */
  localBlobUrl?: string | null;
  canDelete: boolean;
  onDelete: () => void;
  onRetryUpload: () => void;
  // ── PRD.273 Row 1 — transcript + clinical actions (panel-driven, shared state) ──
  /** Whether this recording has a transcript to view. */
  transcriptAvailable: boolean;
  /** Open the transcript drawer for this recording. */
  onViewTranscript: () => void;
  /** Extraction action label (Extract/Refresh/Retry/…). */
  extractionLabel: string;
  extractionDisabled: boolean;
  extractionDisabledReason?: string | null;
  extractionRunning: boolean;
  /** Compact, PHI-free extraction error text (or null). */
  extractionError?: string | null;
  /** Show the extract action at all (Clinical Extraction run permission). */
  canExtract: boolean;
  onExtract: () => void;
  /** Unresolved allergy-review items (PRD.272). 0 hides the action. */
  allergyReviewCount: number;
  onReviewAllergies: () => void;
  canReviewAllergies: boolean;
}

export function AudioRecordingPlayer({
  recording,
  localBlobUrl,
  canDelete,
  onDelete,
  onRetryUpload,
  transcriptAvailable,
  onViewTranscript,
  extractionLabel,
  extractionDisabled,
  extractionDisabledReason,
  extractionRunning,
  extractionError,
  canExtract,
  onExtract,
  allergyReviewCount,
  onReviewAllergies,
  canReviewAllergies,
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [src, setSrc] = useState<string | null>(localBlobUrl ?? null);
  const [urlError, setUrlError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(
    recording.duration_ms ? recording.duration_ms / 1000 : 0,
  );
  const [speedIdx, setSpeedIdx] = useState(1); // default 1×
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [status, setStatus] = useState('');

  const uploaded = recording.upload_status === 'uploaded';
  const uploadFailed = recording.upload_status === 'failed';
  const uploadPending =
    recording.upload_status === 'pending' || recording.upload_status === 'uploading';

  // Resolve an authenticated playback URL when there is no local blob. Re-runs
  // on retry (urlError reset) so an expired URL can be refreshed.
  useEffect(() => {
    if (localBlobUrl) {
      setSrc(localBlobUrl);
      return;
    }
    if (!uploaded || !recording.storage_path) return;
    let cancelled = false;
    setUrlError(false);
    resolvePlaybackUrl(recording.storage_path)
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setUrlError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [localBlobUrl, uploaded, recording.storage_path]);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
    } else {
      el.pause();
    }
  }, []);

  const cycleSpeed = useCallback(() => {
    setSpeedIdx((i) => {
      const next = (i + 1) % SPEEDS.length;
      if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next];
      return next;
    });
  }, []);

  const onSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const el = audioRef.current;
    if (!el) return;
    const t = Number(e.target.value);
    el.currentTime = t;
    setCurrent(t);
  }, []);

  const refreshUrl = useCallback(() => {
    if (!recording.storage_path) return;
    setUrlError(false);
    resolvePlaybackUrl(recording.storage_path)
      .then(setSrc)
      .catch(() => setUrlError(true));
  }, [recording.storage_path]);

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--sp-3)',
        background: 'var(--bg-surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--text)' }}>
          Voice recording
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {formatDuration(duration)} · {formatTimestamp(recording.recorded_at)}
        </div>
      </div>

      {/* Screen-reader live region for async status changes */}
      <div aria-live="polite" style={visuallyHidden}>
        {status}
      </div>

      {/* ── Row 1 — transcript + clinical actions (PRD.273) ── */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
        role="group"
        aria-label="Transcript and clinical actions"
      >
        <button
          type="button"
          onClick={onViewTranscript}
          disabled={!transcriptAvailable}
          aria-label="View transcript"
          style={{ ...pillBtnStyle, opacity: transcriptAvailable ? 1 : 0.5 }}
        >
          View transcript
        </button>
        {canExtract && (
          <button
            type="button"
            onClick={onExtract}
            disabled={extractionDisabled}
            aria-label={extractionLabel}
            title={extractionDisabledReason ?? undefined}
            style={{
              ...pillBtnStyle,
              opacity: extractionDisabled ? 0.5 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {extractionRunning && (
              <span style={{ display: 'inline-flex', animation: 'spin 1s linear infinite' }}>
                <NavIcon name="loader" />
              </span>
            )}
            {extractionLabel}
          </button>
        )}
        {canReviewAllergies && allergyReviewCount > 0 && (
          <button
            type="button"
            onClick={onReviewAllergies}
            aria-label={`${allergyReviewCount} ${allergyReviewCount === 1 ? 'allergy' : 'allergies'} to review`}
            style={pillBtnStyle}
          >
            {allergyReviewCount} {allergyReviewCount === 1 ? 'allergy' : 'allergies'} to review
          </button>
        )}
        {extractionError ? (
          <span role="status" style={{ fontSize: 'var(--font-size-xs)', color: '#dc2626' }}>
            {extractionError}
          </span>
        ) : (
          extractionDisabledReason && (
            <span style={{ fontSize: 'var(--font-size-2xs, 10px)', color: 'var(--text-muted)' }}>
              {extractionDisabledReason}
            </span>
          )
        )}
      </div>

      {src && !urlError ? (
        <>
          <audio
            ref={audioRef}
            src={src}
            preload="metadata"
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (Number.isFinite(d) && d > 0) setDuration(d);
            }}
            onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
            onPlay={() => {
              setPlaying(true);
              setStatus('Playing recording');
            }}
            onPause={() => setPlaying(false)}
            onEnded={() => {
              setPlaying(false);
              setStatus('Playback finished');
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? 'Pause recording' : 'Play recording'}
              style={iconBtnStyle}
            >
              <NavIcon name={playing ? 'pause' : 'play'} />
            </button>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={Math.min(current, duration || 0)}
              onChange={onSeek}
              aria-label="Seek recording"
              style={{ flex: 1, minWidth: 120 }}
            />
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                whiteSpace: 'nowrap',
              }}
            >
              {formatDuration(current)} / {formatDuration(duration)}
            </span>
            <button
              type="button"
              onClick={cycleSpeed}
              aria-label={`Playback speed ${SPEEDS[speedIdx]} times`}
              style={pillBtnStyle}
            >
              {SPEEDS[speedIdx]}×
            </button>
            {canDelete && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                aria-label="Delete recording"
                style={iconBtnStyle}
              >
                <NavIcon name="trash-2" />
              </button>
            )}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {urlError
            ? 'Playback link expired.'
            : uploadPending
              ? 'Waiting to upload…'
              : uploadFailed
                ? 'Upload failed.'
                : 'Preparing playback…'}
          {urlError && (
            <button type="button" onClick={refreshUrl} style={linkBtnStyle}>
              Refresh
            </button>
          )}
        </div>
      )}

      {/* Upload status row */}
      {(uploadPending || uploadFailed) && (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--font-size-xs)' }}
        >
          <span style={{ color: uploadFailed ? '#dc2626' : 'var(--text-muted)' }}>
            {uploadFailed ? 'Upload failed' : 'Waiting to upload'}
          </span>
          {uploadFailed && (
            <button type="button" onClick={onRetryUpload} style={linkBtnStyle}>
              Retry upload
            </button>
          )}
        </div>
      )}

      {confirmDelete && (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--font-size-xs)' }}
        >
          <span>Delete this recording?</span>
          <button
            type="button"
            onClick={() => {
              setConfirmDelete(false);
              onDelete();
            }}
            style={{ ...linkBtnStyle, color: '#dc2626' }}
          >
            Delete
          </button>
          <button type="button" onClick={() => setConfirmDelete(false)} style={linkBtnStyle}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return 'Recorded';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Recorded';
  return `Recorded ${d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  cursor: 'pointer',
};

const pillBtnStyle: React.CSSProperties = {
  minWidth: 40,
  height: 32,
  padding: '0 8px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  marginLeft: 6,
  color: 'var(--color-primary)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
  textDecoration: 'underline',
};

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
